import { EmailTemplatePurpose, ReminderType, SchedulingEmailAudience, SchedulingEmailSlug } from '../../../models/Scheduling'

export interface SchedulingEmailDefinition {
  slug: SchedulingEmailSlug
  purpose: EmailTemplatePurpose
  audience: SchedulingEmailAudience
  label: string
  description: string
  reminderType?: ReminderType
}

export const SCHEDULING_EMAILS: SchedulingEmailDefinition[] = [
  {
    slug: SchedulingEmailSlug.INVITATION_TO_BOOK,
    purpose: EmailTemplatePurpose.INVITATION_TO_BOOK,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Booking invitation',
    description: 'Sent when a team member invites a client to pick a time.',
  },
  {
    slug: SchedulingEmailSlug.BOOKING_REQUEST_DECLINED,
    purpose: EmailTemplatePurpose.BOOKING_REQUEST_DECLINED,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Booking declined',
    description: "Sent when a team member declines a client's booking request.",
  },
  {
    slug: SchedulingEmailSlug.INVITATION_TO_REBOOK,
    purpose: EmailTemplatePurpose.INVITATION_TO_REBOOK,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Rebooking invitation',
    description: 'Sent when a team member asks a client to pick a new time.',
  },
  {
    slug: SchedulingEmailSlug.RESCHEDULE_REQUEST_DECLINED,
    purpose: EmailTemplatePurpose.RESCHEDULE_REQUEST_DECLINED,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Reschedule declined',
    description: "Sent when a team member declines a client's reschedule request.",
  },
  {
    slug: SchedulingEmailSlug.CHANGE_NOTICE,
    purpose: EmailTemplatePurpose.CHANGE_NOTICE,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Meeting details update',
    description: 'Sent when the meeting details change before the client picks a time.',
  },
  {
    slug: SchedulingEmailSlug.CANCELLATION_WITH_MESSAGE,
    purpose: EmailTemplatePurpose.CANCELLATION_WITH_MESSAGE,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Cancellation message',
    description: 'Sent only when a team member cancels a meeting and adds a message.',
  },
  {
    slug: SchedulingEmailSlug.REMINDER_TO_BOOK,
    purpose: EmailTemplatePurpose.REMINDER_TO_BOOK,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Booking reminder',
    description: "Sent when a client hasn't picked a time yet.",
    reminderType: ReminderType.FOLLOW_UP,
  },
  {
    slug: SchedulingEmailSlug.MEETING_REMINDER,
    purpose: EmailTemplatePurpose.MEETING_REMINDER,
    audience: SchedulingEmailAudience.CLIENTS,
    label: 'Meeting reminder',
    description: 'Sent to clients before a scheduled meeting.',
    reminderType: ReminderType.MEETING,
  },
  {
    slug: SchedulingEmailSlug.REQUEST_AWAITING_APPROVAL,
    purpose: EmailTemplatePurpose.REQUEST_AWAITING_APPROVAL,
    audience: SchedulingEmailAudience.ADVISOR,
    label: 'Approval request',
    description: "Sent when a client's request needs approval.",
  },
  {
    slug: SchedulingEmailSlug.NEW_BOOKING_NOTIFICATION,
    purpose: EmailTemplatePurpose.NEW_BOOKING_NOTIFICATION,
    audience: SchedulingEmailAudience.ADVISOR,
    label: 'New booking',
    description: 'Sent when a client books without needing approval.',
  },
  {
    slug: SchedulingEmailSlug.RESCHEDULING_NOTIFICATION,
    purpose: EmailTemplatePurpose.RESCHEDULING_NOTIFICATION,
    audience: SchedulingEmailAudience.ADVISOR,
    label: 'Meeting rescheduled',
    description: 'Sent when a client moves a meeting to a new time.',
  },
  {
    slug: SchedulingEmailSlug.CANCELLATION_NOTIFICATION,
    purpose: EmailTemplatePurpose.CANCELLATION_NOTIFICATION,
    audience: SchedulingEmailAudience.ADVISOR,
    label: 'Client cancellation',
    description: 'Sent when a client cancels on the booking page.',
  },
]

export const REMINDER_EMAIL_SLUG: Record<ReminderType, SchedulingEmailSlug> = {
  [ReminderType.FOLLOW_UP]: SchedulingEmailSlug.REMINDER_TO_BOOK,
  [ReminderType.MEETING]: SchedulingEmailSlug.MEETING_REMINDER,
}

export const schedulingEmailsFor = (audience: SchedulingEmailAudience): SchedulingEmailDefinition[] =>
  SCHEDULING_EMAILS.filter((email) => email.audience === audience)

export const findSchedulingEmail = (slug: string): SchedulingEmailDefinition | undefined => SCHEDULING_EMAILS.find((email) => email.slug === slug)

export const isReminderEmail = (email: SchedulingEmailDefinition): boolean => email.reminderType != null
